<template>
  <FreeBottomSheet
    :name="bottomSheetName"
    @onOpen="analyticsEventsLib.priceCalendarShow()"
  >
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <h2 class="text-text-neutral-primary text-base font-medium">
          {{ t('search.nearestSearchesRoundTrip.title') }}
        </h2>
        <template #rightAction>
          <FreeCloseButton @click="handleCalendarClose(hide)" />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div
        v-if="nearestSearchesData.isLoading.value"
        class="flex min-h-[70vh] items-center justify-center"
      >
        <FreeLoader />
      </div>

      <div v-else class="my-4 flex flex-col justify-center space-y-6">
        <GraphContainer
          :city-name="arrivalCityName"
          :direction="GraphDirectionEnum.TO"
          :searches="roundTripSearches.departureSearches.value"
          :active-search="roundTripSearches.activeDeparture.value"
          @onActiveDateChange="roundTripSearches.updateDepartureSearches"
        />
        <GraphContainer
          :city-name="departureCityName"
          :direction="GraphDirectionEnum.BACK"
          :searches="roundTripSearches.filteredArrivalSearches.value"
          :active-search="roundTripSearches.activeArrival.value"
          :days-difference="roundTripSearches.daysDifference.value"
          @onActiveDateChange="roundTripSearches.updateArrivalSearches"
        />
        <button
          type="button"
          class="green-main-button button-shadow mx-4 flex flex-col items-center justify-center whitespace-nowrap rounded-2xl py-2.5"
          @click="handleNewDatesSubmit"
        >
          <span class="text-lg font-semibold">
            {{ t('search.nearestSearchesRoundTrip.findTickets') }}
          </span>
          <span class="text-[12px] lowercase leading-[14px]">
            {{ priceText }}
          </span>
        </button>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { onBeforeMount, computed } from 'vue';
  import {
    FreeBottomSheet,
    FreeHeader,
    FreeCloseButton,
    FreeLoader,
  } from 'freedom-shared';
  import { controllerModel } from 'shared';
  import { useI18n } from 'vue-i18n';
  import { prettyPrice } from '@aviata/text-fns';
  import type { Dict } from '@aviata/platform-types';
  import type { SearchParams } from '@aviata/platform-client';

  import { analyticsEventsLib } from '@/shared/lib';

  import GraphContainer from './GraphContainer.vue';
  import {
    useRoundTripNearestSearches,
    useGetRoundTripNearestSearches,
  } from '../model';
  import { mapSearchesLib } from '../lib';
  import { GraphDirectionEnum } from '../types';

  const emit = defineEmits<{ onDatesSelect: [depDates: string[]] }>();

  const props = defineProps<{
    dict: Dict.Entity;
    bottomSheetName: string;
    searchQuery: string;
    searchParams: SearchParams;
  }>();

  const { t } = useI18n();

  const bottomSheet = controllerModel.useBottomSheet();

  const nearestSearchesData = useGetRoundTripNearestSearches(
    props.searchParams,
  );

  const roundTripSearches = useRoundTripNearestSearches(
    nearestSearchesData.departureNearestSearches,
    nearestSearchesData.arrivalNearestSearches,
  );

  const departureCityName = computed(() => {
    return mapSearchesLib.getCityName(
      props.searchParams.itineraries[0].dep,
      props.dict,
    );
  });
  const arrivalCityName = computed(() => {
    return mapSearchesLib.getCityName(
      props.searchParams.itineraries[0].arr,
      props.dict,
    );
  });

  const priceText = computed(() => {
    return roundTripSearches.totalPrice.value
      ? t('search.nearestSearchesRoundTrip.priceFrom', {
          minPrice: prettyPrice(roundTripSearches.totalPrice.value.toString()),
        })
      : t('search.nearestSearchesRoundTrip.priceUnknown');
  });

  const handleCalendarClose = (hide: () => void) => {
    analyticsEventsLib.priceCalendarClose();
    hide();
  };

  const handleNewDatesSubmit = () => {
    analyticsEventsLib.priceCalendarOffersShow();
    bottomSheet.hide(props.bottomSheetName);
    emit('onDatesSelect', [
      roundTripSearches.activeDeparture.value.departureDate,
      roundTripSearches.activeArrival.value.departureDate,
    ]);
  };

  onBeforeMount(() => {
    nearestSearchesData
      .init(props.searchQuery)
      .then(() =>
        roundTripSearches.init(nearestSearchesData.selectedDates.value),
      );
  });
</script>

<style scoped>
  .button-shadow {
    box-shadow: 0px 5px 20px 0px #0000000d;
  }
</style>
